import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  VStack,
  Text,
  Center,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Spinner,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FiDatabase, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Header } from "build-ts/metadata";
import { searchPlansheaders, deletePlan, updatePlanHeader } from "services/AnalysisPlanServices";
import { useUser } from "context/UserContext";
import { epochConverterWithTimeZone } from "utils/dateUtils";

const DataQueryList: React.FC = () => {
  const navigate = useNavigate();
  const { defaultTimeZone } = useUser();

  // State
  const [dataQueries, setDataQueries] = useState<Header[]>([]);
  const [totalQueries, setTotalQueries] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Rename modal state
  const [renamingQuery, setRenamingQuery] = useState<Header | null>(null);
  const [newName, setNewName] = useState("");
  const {
    isOpen: isRenameOpen,
    onOpen: onRenameOpen,
    onClose: onRenameClose,
  } = useDisclosure();

  // Delete dialog state
  const [deletingQuery, setDeletingQuery] = useState<Header | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Fetch data queries
  const fetchDataQueries = async (search?: string) => {
    setIsLoading(true);
    try {
      const result = await searchPlansheaders(
        -1, // pageSize - fetch all
        1,  // pageNumber
        "LAST_MODIFIED", // sortByField
        false, // sortAscending (desc for newest first)
        search || "", // searchString
        [], // metricIds
        false, // includeScheduleActivity
        "" // activeSchedule
      );
      if (result) {
        setDataQueries(result.headers);
        setTotalQueries(result.totalRows ?? result.headers.length);
      }
    } catch (error) {
      console.error("Failed to fetch data queries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search on Enter key press
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      fetchDataQueries(searchQuery || undefined);
    }
  };

  useEffect(() => {
    fetchDataQueries();
  }, []);

  // Format date using timezone from user context
  const formatLastModified = (timestamp?: number): string => {
    if (!timestamp) return "";
    return epochConverterWithTimeZone(timestamp, "d-MMM-yyyy, HH:mm", defaultTimeZone) + " " + defaultTimeZone.toUpperCase();
  };

  // Handle data query click - navigate to detail page
  const handleQueryClick = (id: string) => {
    navigate(`/data-query/${id}`);
  };

  // Handle rename click
  const handleRenameClick = (query: Header, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingQuery(query);
    setNewName(query.name || "");
    onRenameOpen();
  };

  // Handle rename submit
  const handleRenameSubmit = async () => {
    if (renamingQuery && newName.trim()) {
      const response = await updatePlanHeader(
        renamingQuery.id!,
        newName.trim(),
        renamingQuery.description
      );
      if (response) {
        // Update local state
        setDataQueries((prev) =>
          prev.map((query) =>
            query.id === renamingQuery.id
              ? { ...query, name: newName.trim() }
              : query
          )
        );
      }
    }
    onRenameClose();
    setRenamingQuery(null);
    setNewName("");
  };

  // Handle delete click
  const handleDeleteClick = (query: Header, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingQuery(query);
    onDeleteOpen();
  };

  // Handle delete submit
  const handleDeleteSubmit = async () => {
    if (deletingQuery) {
      setIsDeleting(true);
      try {
        const response = await deletePlan(deletingQuery.id!);
        if (response?.ok) {
          setDataQueries((prev) =>
            prev.filter((query) => query.id !== deletingQuery.id)
          );
          setTotalQueries((prev) => prev - 1);
        }
      } catch (error) {
        console.error("Failed to delete data query:", error);
      } finally {
        setIsDeleting(false);
      }
    }
    onDeleteClose();
    setDeletingQuery(null);
  };

  return (
    <Box>
      {/* Search Bar */}
      <InputGroup mb={5} size="md">
        <InputLeftElement pointerEvents="none" h="40px">
          <FiSearch size={16} color="#65288b" />
        </InputLeftElement>
        <Input
          placeholder="Search data queries... (Press Enter)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          bg="white"
          border="1px solid"
          borderColor="purple.300"
          borderRadius="lg"
          h="40px"
          fontSize="sm"
          color="purple.900"
          _placeholder={{ color: "purple.400" }}
          _hover={{ borderColor: "purple.400" }}
          _focus={{
            borderColor: "purple.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
          }}
        />
      </InputGroup>

      {/* Query Count */}
      <Flex align="center" justify="space-between" mb={3} px={1}>
        <Text fontSize="sm" color="gray.600" align="left" m={0}>
          {totalQueries} data quer{totalQueries !== 1 ? "ies" : "y"}
        </Text>
      </Flex>

      {/* Data Query List */}
      <Box
        maxH="calc(100vh - 200px)"
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#dfc2f0",
            borderRadius: "2px",
          },
        }}
      >
        {isLoading ? (
          <Flex justify="center" align="center" h="150px">
            <Spinner size="md" color="purple.600" thickness="2px" />
          </Flex>
        ) : dataQueries.length === 0 ? (
          <Center py={16}>
            <VStack spacing={4}>
              <Box p={5} bg="purple.50" borderRadius="full">
                <Icon as={FiDatabase} boxSize={10} color="purple.400" />
              </Box>
              <Text color="gray.600" fontSize="lg" fontWeight="500">
                {searchQuery ? "No data queries found" : "No data queries yet"}
              </Text>
              <Text color="gray.500" fontSize="sm" textAlign="center" maxW="300px">
                {searchQuery
                  ? "Try a different search term"
                  : "Click 'Create New' to create your first data query"}
              </Text>
            </VStack>
          </Center>
        ) : (
          <VStack spacing={2} align="stretch">
            {dataQueries.map((query) => (
              <Flex
                key={query.id}
                p={3}
                align="center"
                borderRadius="lg"
                border="1px solid"
                borderColor={hoveredId === query.id ? "purple.300" : "gray.400"}
                bg={hoveredId === query.id ? "purple.50" : "white"}
                cursor="pointer"
                transition="all 0.15s ease"
                onMouseEnter={() => setHoveredId(query.id!)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => handleQueryClick(query.id!)}
                role="button"
                tabIndex={0}
                _focus={{ outline: "none", bg: "purple.100" }}
                _hover={{ borderColor: "purple.300" }}
              >
                {/* Icon */}
                <Box
                  p={2}
                  bg="purple.100"
                  borderRadius="md"
                  mr={3}
                >
                  <Icon as={FiDatabase} boxSize={4} color="purple.600" />
                </Box>

                {/* Query Info */}
                <Box flex="1" minW={0}>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color="gray.800"
                    noOfLines={1}
                    align="left"
                    m={0}
                  >
                    {query.name || "Untitled"}
                  </Text>
                  <Text fontSize="xs" color="gray.500" align="left" m={0} mt={1}>
                    {formatLastModified(query.last_modified_date_ms)}
                  </Text>
                </Box>

                {/* Actions Menu */}
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<ChevronDownIcon />}
                    variant="ghost"
                    size="sm"
                    color="gray.500"
                    _hover={{ bg: "purple.100", color: "purple.600" }}
                    onClick={(e) => e.stopPropagation()}
                    opacity={hoveredId === query.id ? 1 : 0}
                    transition="opacity 0.15s"
                  />
                  <MenuList
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="md"
                    minW="140px"
                  >
                    <MenuItem
                      icon={<EditIcon color="gray.600" boxSize={4} />}
                      onClick={(e) => handleRenameClick(query, e)}
                      fontSize="sm"
                      py={2}
                      px={3}
                      color="gray.700"
                      fontWeight="500"
                      _hover={{ bg: "gray.50" }}
                    >
                      Rename
                    </MenuItem>
                    <MenuItem
                      icon={<DeleteIcon color="red.500" boxSize={4} />}
                      onClick={(e) => handleDeleteClick(query, e)}
                      fontSize="sm"
                      py={2}
                      px={3}
                      color="red.500"
                      fontWeight="500"
                      _hover={{ bg: "red.50" }}
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>

      {/* Rename Modal */}
      <Modal isOpen={isRenameOpen} onClose={onRenameClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rename Data Query</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Enter new name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  handleRenameSubmit();
                }
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRenameClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleRenameSubmit}
              isDisabled={!newName.trim()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Data Query
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete "{deletingQuery?.name}"? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteSubmit}
                ml={3}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default DataQueryList;
